import { createApi } from '@reduxjs/toolkit/query/react';

import fetchBaseQuery from '@app/api/fetchBaseQuery';
import { constructUrl } from '@app/api/utils';

import type {
    DeleteStyleGuideRequest,
    DownloadStyleGuideRequest,
    EditStyleGuideRequest,
    GetStyleGuidesRequest,
    LanguagesResponse,
    ProjectsResponse,
    StyleGuideResponse,
} from './types';

const styleGuideApi = createApi({
    reducerPath: 'styleGuide',
    baseQuery: fetchBaseQuery(),
    tagTypes: ['StyleGuides'],
    endpoints: (builder) => ({
        getStyleGuides: builder.query<StyleGuideResponse, GetStyleGuidesRequest>({
            query: ({ teamId, ...params }) => ({
                method: 'GET',
                url: constructUrl(`/style-guide/${teamId}`, { ...params }),
            }),
            providesTags: (result) =>
                result
                    ? [...result.map(({ id }) => ({ type: 'StyleGuides' as const, id })), 'StyleGuides']
                    : ['StyleGuides'],
        }),
        getLanguages: builder.query<LanguagesResponse, { teamId: number }>({
            query: ({ teamId }) => ({
                method: 'GET',
                url: `/style-guide/${teamId}/languages`,
            }),
        }),
        getProjects: builder.query<ProjectsResponse, { teamId: number }>({
            query: ({ teamId }) => ({
                method: 'GET',
                url: `/style-guide/${teamId}/projects`,
            }),
        }),
        editStyleGuide: builder.mutation<void, EditStyleGuideRequest>({
            query: ({ teamId, ...body }) => ({
                method: 'PATCH',
                url: `/style-guide/${teamId}/edit`,
                body: body.styleGuides,
            }),
            invalidatesTags: (result, error, arg) =>
                error ? [] : arg.styleGuides.map(({ id }) => ({ type: 'StyleGuides', id })),
        }),
        deleteStyleGuide: builder.mutation<void, DeleteStyleGuideRequest>({
            query: ({ teamId, styleGuideId }) => ({
                method: 'DELETE',
                url: `/style-guide/${teamId}/delete/${styleGuideId}`,
            }),
            invalidatesTags: (result, error, arg) => (error ? [] : [{ type: 'StyleGuides', id: arg.styleGuideId }]),
        }),
        downloadStyleGuide: builder.query<void, DownloadStyleGuideRequest>({
            query: ({ teamId, styleGuideId }) => ({
                method: 'GET',
                url: `/style-guide/${teamId}/download/${styleGuideId}`,
            }),
        }),
        syncStyleGuideWithAI: builder.mutation<void, { teamId: number; styleGuideId: string }>({
            query: ({ teamId, styleGuideId }) => ({
                method: 'POST',
                url: `/style-guide/${teamId}/style-guide/${styleGuideId}/sync`,
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'StyleGuides', id: arg.styleGuideId }],
        }),
    }),
});

export default styleGuideApi;

export const {
    useGetStyleGuidesQuery,
    useGetLanguagesQuery,
    useGetProjectsQuery,
    useEditStyleGuideMutation,
    useDeleteStyleGuideMutation,
    useSyncStyleGuideWithAIMutation,
} = styleGuideApi;

export * from './types';
